import { useState, useRef, useCallback } from "react";

import { startWebRecording, stopWebRecording } from "./webRecorder";
import { startNativeRecording, stopNativeRecording } from "./nativeRecorder";
import useAppView from "../../components/platforms/useAppView";

const useAudioRecorder = () => {
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioURL, setAudioURL] = useState(null);
    const [error, setError] = useState(null);

    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const timerRef = useRef(null);

    const { isNativePlatform } = useAppView();

    // ================= TIMER =================

    const startTimer = () => {
        timerRef.current = setInterval(() => {
            setRecordingTime((t) => t + 1);
        }, 1000);
    };

    const stopTimer = () => {
        if (timerRef.current) clearInterval(timerRef.current);
    };

    // ================= START =================

    const startRecording = useCallback(async () => {
        setError(null);

        try {
            if (isNativePlatform) {
                await startNativeRecording();
            } else {
                mediaRecorderRef.current = await startWebRecording(
                    chunksRef,
                    streamRef
                );
            }

            setRecordingTime(0);
            setIsRecording(true);
            startTimer();
        } catch (err) {
            console.error(err);
            setError(err.message || "Errore avvio registrazione");
        }
    }, [isNativePlatform]);

    // ================= STOP =================

    const stopRecording = useCallback(async () => {
        stopTimer();

        try {
            let blob;

            if (isNativePlatform) {
                blob = await stopNativeRecording();
            } else {
                blob = await stopWebRecording(
                    mediaRecorderRef.current,
                    chunksRef,
                    streamRef
                );
            }

            setAudioBlob(blob);
            setAudioURL(URL.createObjectURL(blob));
            setIsRecording(false);
        } catch (err) {
            console.error(err);
            setError("Errore stop registrazione");
        }
    }, [isNativePlatform]);

    // ================= RESET =================

    const resetRecording = () => {
        if (audioURL) URL.revokeObjectURL(audioURL);
        setAudioBlob(null);
        setAudioURL(null);
        setRecordingTime(0);
    };

    const cleanup = useCallback(async () => {
        stopTimer();

        try {
            // ===== STOP eventuale registrazione attiva =====
            if (isRecording) {
                if (isNativePlatform) {
                    await stopNativeRecording();
                } else if (mediaRecorderRef.current) {
                    mediaRecorderRef.current.stop();
                }
            }

            // ===== stop mic WEB =====
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            }

            // ===== revoke URL =====
            if (audioURL) {
                URL.revokeObjectURL(audioURL);
            }

            // ===== reset state =====
            setIsRecording(false);
            setAudioBlob(null);
            setAudioURL(null);
            setRecordingTime(0);

        } catch (err) {
            console.warn("Cleanup error:", err);
        }
    }, [audioURL, isNativePlatform, isRecording]);

    // ================= FILE =================

    const getAudioFile = () => {
        if (!audioBlob) return null;

        const ext = isNativePlatform ? "m4a" : "webm";
        const fileName = `audio_${Date.now()}.${ext}`;

        return new File([audioBlob], fileName, {
            type: audioBlob.type,
        });
    };

    return {
        isRecording,
        recordingTime,
        audioBlob,
        audioURL,
        error,
        startRecording,
        stopRecording,
        resetRecording,
        getAudioFile,
        cleanup
    };
};

export default useAudioRecorder;
